import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { AIStepLayout } from '../masterclass/ai/AIStepLayout';
import { useProgramCreation } from '../ProgramCreationContext';
import { API } from '../../../../api';
import DosenInput from '../../../../pages/new-designs/components/DosenInput';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { getUserDetails } from '../../../../utils/orgName';

const MENTORSHIP_STEPS = ['Program Name', 'Learning Objectives', 'Members'];

export function MentorshipObjectives() {
  const navigate = useNavigate();
  const { orgId: orgIdParam = '' } = useParams();
  const { state, dispatch } = useProgramCreation();
  const { location } = getUserDetails();
  const [objectives, setObjectives] = useState<string[]>(
    state.mentorship.learningObjectives && state.mentorship.learningObjectives.length > 0
      ? state.mentorship.learningObjectives
      : ['']
  );
  const [maxMatchesPerEmployee, setMaxMatchesPerEmployee] = useState<number>(
    state.mentorship.matchSettings?.maxMatchesPerMentee || 1
  );
  const [maxMatchesPerMentor, setMaxMatchesPerMentor] = useState<number>(
    state.mentorship.matchSettings?.maxMatchesPerMentor || 1
  );
  const [sessionsPerMatch, setSessionsPerMatch] = useState<number>(
    state.mentorship.matchSettings?.sessionsPerMatch || 1
  );
  const [saving, setSaving] = useState(false);

  const trimmedObjectives = useMemo(() => objectives.map((o) => o.trim()), [objectives]);
  const allObjectivesFilled = trimmedObjectives.every((obj) => obj.length > 0);
  const hasAtLeastOneObjective = trimmedObjectives.some((obj) => obj.length > 0);
  const canDelete = objectives.length > 1;

  useEffect(() => {
    if (!state.groupId) {
      navigate('../program-name', { replace: true });
    }
  }, [navigate, state.groupId]);

  const matchValuesValid =
    Number(maxMatchesPerEmployee) > 0 &&
    Number(maxMatchesPerMentor) > 0 &&
    Number(sessionsPerMatch) > 0 &&
    !Number.isNaN(maxMatchesPerEmployee) &&
    !Number.isNaN(maxMatchesPerMentor) &&
    !Number.isNaN(sessionsPerMatch);

  const handleNext = async () => {
    if (!state.groupId) return;
    if (!hasAtLeastOneObjective) {
      toast.error('Adicione pelo menos um learning objective.');
      return;
    }
    if (!matchValuesValid) {
      toast.error('Defina valores válidos para os Match Settings.');
      return;
    }

    setSaving(true);
    try {
      const filteredObjectives = trimmedObjectives.filter(Boolean);

      await API.updateLearningObjectives({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: {
          number: filteredObjectives.length,
          objectives: filteredObjectives
        }
      });

      await API.updateAllMatches({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: {
          maxMatchesPerMentee: Number(maxMatchesPerEmployee),
          maxMatchesPerMentor: Number(maxMatchesPerMentor),
          sessionsPerMatch: Number(sessionsPerMatch),
          matchRequest: false
        }
      });

      await API.updateTopicsAndIndustry({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: { topics: ['Topic Creation'] },
        key: 'topics'
      });
      await API.updateTopicsAndIndustry({
        grpId: state.groupId,
        orgId: orgIdParam,
        data: { industries: ['Industry Creation'] },
        key: 'industries'
      });

      dispatch({
        type: 'UPDATE_MENTORSHIP',
        payload: {
          learningObjectives: filteredObjectives,
          matchSettings: {
            maxMatchesPerMentee: Number(maxMatchesPerEmployee),
            maxMatchesPerMentor: Number(maxMatchesPerMentor),
            sessionsPerMatch: Number(sessionsPerMatch)
          }
        }
      });

      navigate('../members');
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível salvar os Learning Objectives.');
    } finally {
      setSaving(false);
    }
  };

  const updateObjective = (value: string, index: number) => {
    setObjectives((prev) => prev.map((obj, idx) => (idx === index ? value : obj)));
  };

  const addObjective = () => setObjectives((prev) => [...prev, '']);
  const removeObjective = (index: number) => setObjectives((prev) => prev.filter((_, idx) => idx !== index));

  return (
    <AIStepLayout
      stepTitle="Learning Objectives"
      steps={MENTORSHIP_STEPS}
      title="Learning Objectives"
      onBack={() => navigate(-1)}
      onNext={handleNext}
      nextDisabled={saving || !allObjectivesFilled || !matchValuesValid}
      nextLabel="Save & Continue"
    >
      <Typography
        sx={{
          color: 'var(--Text-text-secondary, #B6B6B6)',
          fontSize: 14,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          lineHeight: '19.88px',
          wordWrap: 'break-word'
        }}
      >
        Capture what mentors and mentees should achieve with this mentorship. Use short, action-oriented objectives.
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {objectives.map((obj, index) => (
          <Box key={index} display="flex" gap={1} alignItems="center">
            <DosenInput
              fullWidth
              label={`Learning Objective ${index + 1}`}
              value={obj}
              onChange={(e) => updateObjective(e.target.value, index)}
            />
            {canDelete && (
              <IconButton onClick={() => removeObjective(index)} aria-label="Remove learning objective">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}

        <Box display="flex">
          <DosenButton iconOnly tone="basic" onClick={addObjective} aria-label="Add learning objective">
            <AddIcon sx={{ fontSize: 14, color: '#FDFDFD' }} />
          </DosenButton>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={2} pt={2}>
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif'
          }}
        >
          Match Settings
        </Typography>

        <DosenInput
          label="Maximum matches per employee"
          type="number"
          inputProps={{ min: 1 }}
          value={maxMatchesPerEmployee}
          onChange={(e) => setMaxMatchesPerEmployee(Number(e.target.value))}
        />

        <DosenInput
          label="Maximum matches per mentor"
          type="number"
          inputProps={{ min: 1 }}
          value={maxMatchesPerMentor}
          onChange={(e) => setMaxMatchesPerMentor(Number(e.target.value))}
        />

        <DosenInput
          label="Required sessions per match"
          type="number"
          inputProps={{ min: 1 }}
          value={sessionsPerMatch}
          onChange={(e) => setSessionsPerMatch(Number(e.target.value))}
        />
      </Box>
    </AIStepLayout>
  );
}

export default MentorshipObjectives;
